import { Link, useNavigate } from "react-router-dom";
import apiAgent from "../../api/apiAgent";
import { tMsg } from "../ToastNotifications";
import { useEffect, useState } from "react";
import _ from "lodash";
import { IListItemProps } from "../interfaces";
import { ImageWithLoading } from "../ImageWithLoading";
import { useSetChangesMade } from "../Publishing/utils";
import { DeletePopup } from "../ConfirmationPopup";
import {
  checkTitle,
  mergeClass,
  removeTags,
  useFetchThumbFromMediaItem,
} from "../../utils";
import parse from "html-react-parser";

export const ListItem = ({
  item,
  modelName,
  returnModel,
  returnId,
  refreshList,
  isCard,
  linkParams,
}: IListItemProps): JSX.Element => {
  const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);
  const [imageSrc, setImageSrc] = useState<string | undefined>(undefined);
  const fetchThumb = useFetchThumbFromMediaItem();
  const setChangesMade = useSetChangesMade(returnModel || modelName);

  useEffect(() => {
    const getSrc =
      item.thumbnail || item.media_title_image || item.image || undefined;
    if (!getSrc && modelName !== "media")
      fetchThumb(modelName, item.id, setImageSrc);
    setImageSrc(getSrc);
  }, [item, modelName]);

  const rawItemName = item.cardTitle
    ? item.cardTitle
    : item.name
    ? item.name
    : item.title
    ? item.title
    : item.caption
    ? removeTags(item.caption)
    : item.media_type
    ? item.media_type
    : null;

  const truncatedName = parse(
    _.truncate(rawItemName, { length: isCard ? 50 : 120 })
  );
  const itemName =
    rawItemName === null ? null : modelName === "moment" ||
      modelName === "landmark" ? (
      <div
        className={mergeClass({
          "flex flex-row gap-4": !isCard,
          "px-2": isCard,
        })}
      >
        <div className="font-bold">{item.date}</div>
        <div>{truncatedName}</div>
      </div>
    ) : (
      truncatedName
    );

  const userRole = modelName === "users" ? item.user_role : undefined;

  const handleDeletePressed = async () => {
    try {
      if (!item.id) throw "No ID specified.";
      // const request =
      //   modelName === "users"
      //     ? apiAgent.Users.delete(modelName, { username: item.name })
      //     : apiAgent.DataType.delete(modelName, {
      //         targetIdx: _.toNumber(item.id),
      //       });
      const request = apiAgent.DataType.delete({
        model_name: modelName,
        target_ids: [_.toNumber(item.id)],
      });
      const response = await request;
      if (response.status === 200) {
        tMsg.success(`${checkTitle(modelName)} deleted.`);
        setChangesMade();
        if (refreshList) refreshList();
      }
    } catch (err) {
      // TODO: add any error in response to notifications here
      tMsg.error(`Error deleting ${modelName}`);
      console.log(err);
    }
  };

  const addParams =
    returnModel && returnId
      ? `?from=${returnModel}&id=${returnId}&${linkParams}`
      : linkParams
      ? `?${linkParams}`
      : "";

  const mainUrl = `/m/${modelName}/show/${item.id}${addParams}`;
  const editUrl = `/m/${modelName}/edit/${item.id}${addParams}`;

  const displayProps = {
    itemName,
    imageSrc,
    userRole,
    mainUrl,
    editUrl,
    handleDeletePressed: () => setShowDeletePopup(true),
    addNewTag:
      (modelName === "moment" || modelName === "landmark") &&
      item.marked_as_new === true,
  };

  return (
    <>
      <div
        className={mergeClass("text-center bg-gray-400", {
          "opacity-70": item.hidden,
        })}
      >
        {isCard ? (
          <CardListItem {...displayProps} />
        ) : (
          <TextListItem {...displayProps} noThumb={modelName === "users"} />
        )}
      </div>
      <DeletePopup
        confirmAction={() => {
          handleDeletePressed();
          setShowDeletePopup(false);
        }}
        setShowModal={setShowDeletePopup}
        showModal={showDeletePopup}
      />
    </>
  );
};

interface IItemDisplayProps {
  imageSrc: string | undefined;
  itemName: string | React.ReactNode | undefined;
  userRole: string | undefined;
  mainUrl: string;
  editUrl: string;
  // handleMainNavigate: () => void;
  // handleEditPressed: () => void;
  handleDeletePressed: () => void;
  addNewTag?: boolean; // for NMA defining moments - moment model only
  noThumb?: boolean;
}

const CardListItem = ({
  imageSrc,
  itemName,
  userRole,
  mainUrl,
  editUrl,
  // handleMainNavigate,
  // handleEditPressed,
  handleDeletePressed,
  addNewTag,
}: IItemDisplayProps) => (
  <div className="transition w-48 h-64 group relative">
    <a href={mainUrl}>
      <div
        // onClick={() => handleMainNavigate()}
        className="flex flex-col w-full h-full"
      >
        <div className="h-3/4 bg-black w-full pointer-events-none overflow-hidden relative">
          {imageSrc && (
            <ImageWithLoading
              src={imageSrc || "/assets/default-image.jpg"}
              className="w-full h-full object-cover"
            />
          )}
          {addNewTag && (
            <img
              src="/assets/new_flag.png"
              className="absolute right-0 top-0"
            />
          )}
        </div>
        {userRole ? (
          <div className="flex flex-col bg-grayBg w-full h-1/4 text-white">
            <div className="grid place-content-center">{itemName}</div>
            <div className="grid place-content-center">{userRole}</div>
          </div>
        ) : (
          <div className="bg-grayBg w-full max-w-full h-1/4 grid place-content-center text-white overflow-hidden">
            {itemName}
          </div>
        )}
        <div className="transition absolute top-0 left-0 w-full h-full bg-black/10 opacity-0 group-hover:opacity-100 pointer-events-none" />
      </div>
    </a>
    <div className="transition absolute top-0 right-0 flex opacity-0 group-hover:opacity-100 group-hover:pointer-events-auto">
      <a href={editUrl}>
        <div
          className="text-black bg-white hover:bg-[#EEEEEE] p-3"
          // onClick={() => handleEditPressed()}
        >
          <i className="icon-edit" />
        </div>
      </a>
      <div
        className="text-warningRed bg-white hover:bg-[#EEEEEE] p-3 cursor-pointer"
        onClick={() => handleDeletePressed()}
      >
        <i className="icon-delete" />
      </div>
    </div>
  </div>
);

const TextListItem = ({
  imageSrc,
  itemName,
  mainUrl,
  editUrl,
  // handleMainNavigate,
  // handleEditPressed,
  handleDeletePressed,
  addNewTag,
  noThumb,
}: IItemDisplayProps) => (
  <div className="transition w-full h-12 text-left">
    <div className="flex w-full">
      <a href={mainUrl} className="flex-grow flex group">
        {!noThumb && (
          <div className="w-[3.5rem] relative transition">
            {imageSrc && (
              <ImageWithLoading
                src={imageSrc || "/assets/default-image.jpg"}
                className="w-full h-12 object-cover"
                listThumb
              />
            )}
            {addNewTag && (
              <img
                src="/assets/new_flag.png"
                className="absolute right-0 top-0 w-1/2"
              />
            )}
          </div>
        )}
        <div
          className="transition w-full h-full bg-grayBg group-hover:bg-[#999] text-white px-4 grid content-center relative group"
          // onClick={() => handleMainNavigate()}
        >
          {itemName}
          {/* <div className="transition absolute top-0 left-0 w-full h-full bg-black/10 opacity-0 group-hover:opacity-100 pointer-events-none" /> */}
        </div>
      </a>
      <a href={editUrl}>
        <div
          className="text-black bg-grayBg hover:bg-[#ccc] p-3"
          // onClick={() => handleEditPressed()}
        >
          <i className="icon-edit" />
        </div>
      </a>
      <div
        className="text-warningRed bg-grayBg hover:bg-[#ccc] p-3 cursor-pointer"
        onClick={() => handleDeletePressed()}
      >
        <i className="icon-delete" />
      </div>
    </div>
  </div>
);
